import React, { useEffect, useState, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import NavBar from './NavBar';
import YouTubeButton from './YouTubeButton';
import "./Artisti.css";
import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import Footer from './Footer';
import CustomAudio from "./CustomAudio";

export default function ArtistDetail() {
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const forceFallback = searchParams.get('demoFallback') === '1';
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackIndexByAlbum, setTrackIndexByAlbum] = useState({}); // { [albumIdx]: number }
  // Gestione stato pulsanti attivi (per mantenere la luce pulsante dopo il click)
  // Refs dei player audio per album
  const audioRefs = useRef({});
  const [showIntroForIdx, setShowIntroForIdx] = useState(null); // indice album per overlay fullscreen
  // Refs dei video monitor per album
  const videoRefs = useRef({});
  const [videoError, setVideoError] = useState({}); // { [idx]: true }
  const fsVideoRef = useRef(null); // video fullscreen corrente
  // Refs del contenitore card per aggiornare stato visuale (es. animazioni fallback)
  const albumRefs = useRef({});
  // Evita doppia esecuzione tra pointerdown e click
  const lastPlayGestureTsRef = useRef(0);
  const [audioUiMsg, setAudioUiMsg] = useState(null);

  // Helper: apri fullscreen e metti in pausa il CRT per evitare doppia riproduzione
  const openFullscreen = (idx) => {
    try {
      const v = videoRefs.current[idx];
      if (v) v.pause();
    } catch {}
    setShowIntroForIdx(idx);
  };

  // Helper: chiudi fullscreen sincronizzando stato e tempo su audio + CRT
  const closeFullscreenSync = () => {
    const i = showIntroForIdx;
    try {
      const fs = fsVideoRef.current;
      const a = audioRefs.current[i]?.element;
      const crt = videoRefs.current[i];
      if (fs) {
        const t = fs.currentTime || 0;
        if (a) a.currentTime = t;
        if (crt) crt.currentTime = t;
        if (fs.paused) {
          if (a) audioRefs.current[i]?.pause?.();
          if (crt) crt.pause();
        } else {
          if (a) audioRefs.current[i]?.play?.();
          if (crt) crt.play().catch(() => {});
        }
      }
    } catch {}
    setShowIntroForIdx(null);
  };

  // Quando overlay è aperto: ascolta play/pause dell'audio per riflettere sul fullscreen
  useEffect(() => {
    if (showIntroForIdx === null) return;
    const i = showIntroForIdx;
    const a = audioRefs.current[i]?.element;
    if (!a) return;
    const onAPlay = () => { try { fsVideoRef.current && fsVideoRef.current.play().catch(() => {}); } catch {} };
    const onAPause = () => { try { fsVideoRef.current && fsVideoRef.current.pause(); } catch {} };
    const onATime = () => {
      try {
        const v = fsVideoRef.current;
        if (!v) return;
        const delta = Math.abs(((a.currentTime || 0) - (v.currentTime || 0)));
        if (delta > 0.35) {
          v.currentTime = a.currentTime || 0;
        }
      } catch {}
    };
    a.addEventListener('play', onAPlay);
    a.addEventListener('pause', onAPause);
    a.addEventListener('timeupdate', onATime);
    return () => {
      a.removeEventListener('play', onAPlay);
      a.removeEventListener('pause', onAPause);
      a.removeEventListener('timeupdate', onATime);
    };
  }, [showIntroForIdx]);

  // Sync continuo: l'audio è la sorgente primaria; i mini monitor CRT seguono tempo e stato
  useEffect(() => {
    let rafId;
    const tick = () => {
      try {
        const keys = Object.keys(audioRefs.current || {});
        for (const k of keys) {
          const i = Number(k);
          const audioEl = audioRefs.current[i]?.element;
          const vid = videoRefs.current[i];
          const card = albumRefs.current[i];
          if (!audioEl) continue;
          const aPaused = audioEl.paused;
          // Se esiste il video, sincronizza con l'audio
          if (vid) {
            // Mantenere video sempre muto
            try { vid.muted = true; vid.defaultMuted = true; vid.volume = 0; } catch {}
            const vPaused = vid.paused;
            // Do not auto-start video without user gesture; only pause if audio paused
            if (aPaused && !vPaused) {
              vid.pause();
            }
            // Allinea il tempo
            const aT = audioEl.currentTime || 0;
            const vT = vid.currentTime || 0;
            const delta = Math.abs(aT - vT);
            if (delta > 0.25) {
              vid.currentTime = aT;
            }
          }
          // Aggiorna classe visiva per fallback/animazioni usando solo lo stato audio
          try { if (card) { if (!aPaused) { card.classList.add('is-playing'); } else { card.classList.remove('is-playing'); } } } catch {}
        }
      } catch {}
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    return () => { try { window.cancelAnimationFrame(rafId); } catch {} };
  }, []);

  // removed legacy activeBtnKeys visual toggle; glow handled via CSS

  // Mobile-friendly tooltip: show label briefly only for touch/pen
  const showLabelHint = (e) => {
    if (e && e.pointerType && (e.pointerType === 'touch' || e.pointerType === 'pen')) {
      const el = e.currentTarget;
      if (!el) return;
      el.classList.add('show-label');
      setTimeout(() => {
        try { el.classList.remove('show-label'); } catch {}
      }, 1600);
    }
  };

  // Tooltip standard gestito via CSS su :hover, :focus, :active

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const ref = doc(db, "artisti", id);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          if (active) setArtist(null);
        } else {
          if (active) setArtist({ id: snap.id, ...snap.data() });
        }
      } catch (e) {
        console.warn("ArtistDetail: errore caricamento", e);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return <div className="container" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>Caricamento...</div>;
  }
  if (!artist) {
    return (
      <div className="container" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexDirection: 'column', gap: 16 }}>
        Artista non trovato
      </div>
    );
}

  // Heuristic to decide if a URL looks like a direct audio file
  const isPlayableAudioUrl = (u) => {
    if (!u || typeof u !== 'string') return false;
    const qIndex = u.indexOf('?');
    const path = qIndex >= 0 ? u.slice(0, qIndex) : u;
    // Support common audio containers and Safari HLS
    return /\.(mp3|m4a|aac|wav|ogg|oga|webm|mp4|m3u8)$/i.test(path);
  };

  // Render principale artista
  return (
    <div className="publicsite-bg artist-detail">
      {/* Header/logo + menu come nelle altre pagine pubbliche */}
      <div className="logo-wrapper">
        <div className="logo-stack">
          <img src="/disco.png" alt="Disco" className="disco-img" />
          <img src="/logo.png" alt="Logo Arte Registrazioni" className="logo-img" />
        </div>
      </div>
      <NavBar />
      

      <div className="container">
      {/* Header bio + foto */}
      <div className="detail-grid" style={{ marginBottom: 22 }}>
        {artist.photo && (
          <div className="artist-photo">
            <img src={artist.photo} alt={artist.nome || artist.name || 'Foto artista'} />
          </div>
        )}
        <div className="bio-col">
          <div className="artist-name">{artist.nome || artist.name}</div>
          <div className="bio-box">
            <div className="artist-description bio-text" style={{ color: '#fcfbfb', fontSize: '1.22em', lineHeight: 1.7 }}>{artist.bio}</div>
          </div>
        </div>
      </div>

      {/* Elenco dischi: cover con descrizione sotto e pulsanti */}
      {(artist.albums || []).length > 0 && (
        <div style={{ marginTop: 8 }}>
          {(artist.albums || []).map((album, idx) => {
            const tracks = Array.isArray(album.tracks) ? album.tracks : [];
            const hasTracks = tracks.length > 0;
            const tIdx = trackIndexByAlbum[idx] ?? 0;
            const currentTrack = hasTracks ? tracks[Math.min(tIdx, tracks.length - 1)] : null;
            const playBtn = !hasTracks && album.buttons && album.buttons.find(b => (b.name || "").toLowerCase() === "play" && b.link);
            const trackCount = tracks.length;
            const isSingle = (trackCount === 1) || (!hasTracks && !!playBtn);
            const priceLabel = isSingle ? 'Singolo € 1,99' : 'Album € 9,99';
            // Determine a playable audio src if present
            const candidateSrc = hasTracks ? (currentTrack?.link || '') : (playBtn?.link || '');
            const hasPlayableAudio = isPlayableAudioUrl(candidateSrc);
            // Fallback: if there are tracks but none are direct audio files, pick first link for external play
            const firstTrackLink = hasTracks ? (tracks[0]?.link || '') : '';
            return (
              <div key={idx} className="album-card" ref={(el) => { if (el) albumRefs.current[idx] = el; }}>
                {album.cover && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div className="cover-frame">
                      <img src={album.cover} alt={album.title} />
                    </div>
                  </div>
                )}
                <div className="cover-info" style={{ color: '#fff' }}>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <span><strong>Titolo:</strong> {album.title}</span>
                    <span><strong>Anno:</strong> {album.year}</span>
                    <span><strong>Genere:</strong> {album.genre}</span>
                  </div>
                </div>
                {hasPlayableAudio && (
                  <div style={{ marginTop: 8, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <CustomAudio
                      ref={(node) => {
                        if (node) {
                          audioRefs.current[idx] = node;
                        } else {
                          delete audioRefs.current[idx];
                        }
                      }}
                      src={candidateSrc}
                      icons={{ play: '/icons/play4.png', pause: '/icons/play4.png' }}
                      showButton={false}
                    />
                    <div className="playing-badge" style={{ display: 'none', color: '#ffd700', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.12)', padding: '4px 10px', borderRadius: 8, fontWeight: 800, letterSpacing: 0.4, boxShadow: '0 0 12px rgba(255,215,0,0.4)' }}>
                      Riproducendo…
                    </div>
                  </div>
                )}
                {/* Azioni: griglia pulsanti centrata con prezzo sotto il Download */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div className="icon-grid">
                  {/* Play */}
                  {hasPlayableAudio ? (
                    <button
                      type="button"
                      className="icon-cell icon-cell--play pulse-on-hover"
                      onPointerDown={async (e) => {
                        // Usa pointerdown per agganciarsi al gesto utente su iOS
                        showLabelHint(e);
                        const nowTs = Date.now();
                        lastPlayGestureTsRef.current = nowTs;
                        setAudioUiMsg(null);
                        try {
                          // Pausa tutti gli altri
                          const keys = Object.keys(audioRefs.current || {});
                          for (const k of keys) {
                            const i = Number(k);
                            if (i !== idx) { audioRefs.current[i]?.pause?.(); }
                          }
                          const ref = audioRefs.current[idx];
                          if (ref && ref.element) {
                            try { ref.element.muted = false; ref.element.volume = 1; } catch {}
                            // Play immediato legato al gesto
                            await ref.play?.();
                          }
                          // Sync video dopo l'avvio audio
                          const v = videoRefs.current[idx];
                          const ael = audioRefs.current[idx]?.element;
                          if (ael && v) {
                            v.muted = true; v.defaultMuted = true; v.volume = 0;
                            v.currentTime = ael.currentTime || 0;
                            if (ael.paused) { v.pause(); } else { try { await v.play(); } catch {} }
                          }
                        } catch (err) {
                          // Mostra suggerimento per iOS (mute/silent switch)
                          const name = err && (err.name || err.code) || '';
                          if (name === 'NotAllowedError' || name === 'AbortError') {
                            setAudioUiMsg('Se la musica non parte, disattiva il silenzioso/suona e tocca di nuovo Play.');
                          } else {
                            setAudioUiMsg('Impossibile avviare l\'audio. Riprova.');
                          }
                        }
                      }}
                      onClick={async () => {
                        // Evita doppio trigger se pointerdown è appena avvenuto
                        if (Date.now() - lastPlayGestureTsRef.current < 250) return;
                        setAudioUiMsg(null);
                        try {
                          // Fallback classico click
                          const keys = Object.keys(audioRefs.current || {});
                          for (const k of keys) {
                            const i = Number(k);
                            if (i !== idx) { audioRefs.current[i]?.pause?.(); }
                          }
                          const ref = audioRefs.current[idx];
                          if (ref && typeof ref.toggle === 'function') {
                            const aEl = ref.element;
                            if (aEl && aEl.paused) {
                              try { aEl.muted = false; aEl.volume = 1; } catch {}
                              try { await ref.play?.(); } catch {}
                            } else {
                              ref.toggle();
                            }
                          }
                          const v = videoRefs.current[idx];
                          const ael = audioRefs.current[idx]?.element;
                          if (ael && v) {
                            v.muted = true; v.defaultMuted = true; v.volume = 0;
                            v.currentTime = ael.currentTime || 0;
                            if (ael.paused) { v.pause(); } else { v.play().catch(() => {}); }
                          }
                        } catch (err) {
                          const name = err && (err.name || err.code) || '';
                          if (name === 'NotAllowedError' || name === 'AbortError') {
                            setAudioUiMsg('Se la musica non parte, disattiva il silenzioso/suona e tocca di nuovo Play.');
                          } else {
                            setAudioUiMsg('Impossibile avviare l\'audio. Riprova.');
                          }
                        }
                      }}
                      aria-label="Play/Pause"
                      data-label="Play/Pause"
                    >
                      <img src="/icons/play4.png" alt="Play" />
                    </button>
                  ) : playBtn ? (
                    <a
                      className="icon-cell icon-cell--play pulse-on-hover"
                      onPointerDown={showLabelHint}
                      href={playBtn.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Play"
                      data-label="Play"
                    >
                      <img src="/icons/play4.png" alt="Play" />
                    </a>
                  ) : hasTracks && firstTrackLink ? (
                    <a
                      className="icon-cell icon-cell--play pulse-on-hover"
                      onPointerDown={showLabelHint}
                      href={firstTrackLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Play"
                      data-label="Play"
                    >
                      <img src="/icons/play4.png" alt="Play" />
                    </a>
                  ) : null}
                  {/* Spotify */}
                  {album.buttons?.some(b => (b.name || '').toLowerCase() === 'spotify') && (
                    <a className="icon-cell pulse-on-hover" onPointerDown={showLabelHint} href={(album.buttons.find(b => (b.name || '').toLowerCase() === 'spotify') || {}).link} target="_blank" rel="noopener noreferrer" aria-label="Spotify" data-label="Spotify">
                      <img src="/icons/spotify1.png" alt="Spotify" />
                    </a>
                  )}
                  {/* Apple */}
                  {album.buttons?.some(b => (b.name || '').toLowerCase().includes('apple')) && (
                    <a className="icon-cell pulse-on-hover" onPointerDown={showLabelHint} href={(album.buttons.find(b => (b.name || '').toLowerCase().includes('apple')) || {}).link} target="_blank" rel="noopener noreferrer" aria-label="Apple Music" data-label="Apple Music">
                      <img src="/icons/apple3.png" alt="Apple Music" />
                    </a>
                  )}
                  {/* YouTube */}
                  {album.buttons?.some(b => (b.name || '').toLowerCase().includes('youtube')) && (
                    <a className="icon-cell pulse-on-hover" onPointerDown={showLabelHint} href={(album.buttons.find(b => (b.name || '').toLowerCase().includes('youtube')) || {}).link} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-label="YouTube">
                      <img src="/icons/youtube2.png" alt="YouTube" />
                    </a>
                  )}
                  {/* Download */}
                  {album.paymentLinkUrl ? (
                    <a className="icon-cell icon-cell--download pulse-on-hover" onPointerDown={showLabelHint} href={album.paymentLinkUrl} target="_blank" rel="noopener noreferrer" aria-label="Buy & Download" data-label="Buy & Download" data-price={priceLabel}>
                      <img src="/icons/download5.png" alt="Buy & Download" />
                    </a>
                  ) : (album.paypalHostedButtonId || true) ? (
                    <form action="https://www.paypal.com/cgi-bin/webscr" method="post" target="_top" className="icon-cell icon-cell--download pulse-on-hover" style={{ display: 'inline-flex' }} onPointerDown={showLabelHint} aria-label="Buy & Download" data-label="Buy & Download" data-price={priceLabel}>
                      <input type="hidden" name="cmd" value="_s-xclick" />
                      <input type="hidden" name="hosted_button_id" value={album.paypalHostedButtonId || (isSingle ? '5CGE5SB2DM2G2' : 'P7FKWUEHQGCFE')} />
                      <input type="hidden" name="currency_code" value="EUR" />
                      <input type="hidden" name="custom" value={`${artist.id || ''}:${idx}`} />
                      <input type="hidden" name="return" value={(typeof window !== 'undefined' ? window.location.origin : '') + `/download-confirm?cm=${encodeURIComponent(`${artist.id || ''}:${idx}`)}`} />
                      <input type="hidden" name="cancel_return" value={(typeof window !== 'undefined' ? window.location.origin : '') + '/artisti'} />
                      <button type="submit" title="Paga con PayPal" style={{ background: 'transparent', border: 'none', padding: 0, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', cursor: 'pointer' }}>
                        <img src="/icons/download5.png" alt="Buy & Download" />
                      </button>
                    </form>
                  ) : (
                    album.buttons?.some(b => (b.name || '').toLowerCase().includes('download')) && (
                      <a className="icon-cell icon-cell--download pulse-on-hover" onPointerDown={showLabelHint} href={(album.buttons.find(b => (b.name || '').toLowerCase().includes('download')) || {}).link} target="_self" rel="noopener noreferrer" aria-label="Buy & Download" data-label="Buy & Download" data-price={priceLabel}>
                        <img src="/icons/download5.png" alt="Buy & Download" />
                      </a>
                    )
                  )}
                  </div>
                </div>
                

                {/* Messaggio UI per problemi audio (iOS/silent mode) */}
                {audioUiMsg && (
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
                    <div style={{ color: '#ffe9a6', background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.08)', padding: '6px 10px', borderRadius: 8, fontWeight: 700 }}>
                      {audioUiMsg}
                    </div>
                  </div>
                )}

                {/* Mini monitor CRT quadrato con pulsante fullscreen 9:16 */}
                <div className="album-monitor crt" style={{ marginTop: 10 }}>
                  {(() => {
                    const hasError = !!videoError[idx];
                    const monitorSrc = (!forceFallback && !hasError && album.videoUrl) ? album.videoUrl : null;
                    if (monitorSrc) {
                      return (
                        <video
                          ref={(el) => {
                            if (el) {
                              videoRefs.current[idx] = el;
                              try { el.muted = true; el.defaultMuted = true; el.volume = 0; } catch {}
                            }
                          }}
                          src={monitorSrc}
                          muted
                          playsInline
                          preload="metadata"
                          controls={false}
                          onError={() => setVideoError(prev => ({ ...prev, [idx]: true }))}
                          onEnded={() => { try { videoRefs.current[idx].currentTime = 0; } catch {} }}
                        />
                      );
                    }
                    return (
                      <div className="album-monitor-fallback">
                        <div className="fallback-disc">
                          <img src={album.cover || '/disco.png'} alt="disc" />
                          <div className="fallback-center"></div>
                        </div>
                        <div className="fallback-eq">
                          <span></span><span></span><span></span><span></span><span></span>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Floating fullscreen button inside CRT (only if a video is available) */}
                  {album.videoUrl && !videoError[idx] && !forceFallback && (
                    <button
                      type="button"
                      className="monitor-fs-fab"
                      onClick={(e) => { e.stopPropagation(); openFullscreen(idx); }}
                      aria-label="Apri a tutto schermo"
                      title="Fullscreen"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 5H5v4M15 5h4v4M9 19H5v-4M15 19h4v-4" stroke="#ffd700" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  )}
                </div>

                {hasTracks && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 8, color: '#ffd700' }}>
                    <button type="button" className="icon-cell" aria-label="Traccia precedente" onClick={() => setTrackIndexByAlbum(prev => ({ ...prev, [idx]: Math.max(0, (prev[idx] ?? 0) - 1) }))} disabled={(trackIndexByAlbum[idx] ?? 0) <= 0}>
                      ◀
                    </button>
                    <div style={{ minWidth: 120, textAlign: 'center', padding: '2px 8px', background: '#111', border: '1px solid #444', borderRadius: 6 }}>
                      {currentTrack?.title || `Traccia ${tIdx + 1}`}
                    </div>
                    <button type="button" className="icon-cell" aria-label="Traccia successiva" onClick={() => setTrackIndexByAlbum(prev => ({ ...prev, [idx]: Math.min((tracks.length - 1), (prev[idx] ?? 0) + 1) }))} disabled={(trackIndexByAlbum[idx] ?? 0) >= (tracks.length - 1)}>
                      ▶
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showIntroForIdx !== null && (
        <div className="monitor-fs-overlay" role="dialog" aria-modal="true" onClick={closeFullscreenSync}>
          <button type="button" className="monitor-fs-close" onClick={(e) => { e.stopPropagation(); closeFullscreenSync(); }} aria-label="Chiudi">Chiudi</button>
          <div className="monitor-fs-frame" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const idx = showIntroForIdx;
              const album = (artist.albums || [])[idx] || {};
              const hasError = !!videoError[idx];
              const monitorSrc = (!hasError && album.videoUrl) ? album.videoUrl : null;
              if (monitorSrc) {
                return (
                  <video
                    ref={(el) => {
                      fsVideoRef.current = el;
                      try { if (el) { el.muted = true; el.defaultMuted = true; el.volume = 0; } } catch {}
                    }}
                    src={monitorSrc}
                    muted
                    playsInline
                    preload="metadata"
                    controls
                    autoPlay
                    onLoadedMetadata={() => {
                      try {
                        const i = idx;
                        const a = audioRefs.current[i]?.element;
                        if (a && fsVideoRef.current && !Number.isNaN(a.currentTime)) {
                          fsVideoRef.current.currentTime = a.currentTime;
                        }
                      } catch {}
                    }}
                    onPlay={() => {
                      try {
                        const i = idx;
                        audioRefs.current[i]?.play?.();
                      } catch {}
                    }}
                    onPause={() => {
                      try {
                        const i = idx;
                        audioRefs.current[i]?.pause?.();
                      } catch {}
                    }}
                    onSeeked={() => {
                      try {
                        const i = idx;
                        const a = audioRefs.current[i]?.element;
                        const v = fsVideoRef.current;
                        if (a && v && !Number.isNaN(v.currentTime)) {
                          a.currentTime = v.currentTime;
                        }
                      } catch {}
                    }}
                    onTimeUpdate={() => {
                      try {
                        const i = idx;
                        const a = audioRefs.current[i]?.element;
                        const v = fsVideoRef.current;
                        if (a && v) {
                          const delta = Math.abs((a.currentTime || 0) - (v.currentTime || 0));
                          if (delta > 0.6) {
                            a.currentTime = v.currentTime || 0;
                          }
                        }
                      } catch {}
                    }}
                    onError={() => setVideoError(prev => ({ ...prev, [idx]: true }))}
                  />
                );
              }
              return (
                <div style={{ color: '#fff', textAlign: 'center', padding: 20 }}>
                  Nessun video disponibile per questo album.
                </div>
              );
            })()}
          </div>
        </div>
      )}
      </div>
      <div className="youtube-under-menu">
        <YouTubeButton small layout="row" />
      </div>
      <Footer />
    </div>
  );

}
